import { useFieldArray, useForm } from "react-hook-form";
import { CategoryModal } from "../CategoryGrid";
import * as React from "react";
import { useState } from "react";
import { getCurrencyCode, getProductCodeTitle } from "../Category/helpers";
import { useGetProductCategoriesPerLocation } from "../../../../../../hooks/query";
import { FormInput, FormSelect, NumericFormInputGroup } from "../../../../../../components/Forms";
import { useTranslation } from "react-i18next";
import { CURRENCY_SYMBOLS, nonNumericSymbols, showErrorToast } from "../../../../../../utils";
import { useToast } from "@chakra-ui/react";

function PurchasedAndCapitalGoodsModalWrapper({ tabKey, modalData, attributesOrder, isOpen, onClose, onSubmit, locations }) {
  const toast = useToast();
  const { fieldArrayName, editData } = modalData;
  const { t } = useTranslation("translation", {
    keyPrefix: "organizationQuestionnaire.scope3"
  });
  const { t: globalTranslation } = useTranslation("translation");

  const [refetchLocation, setRefetchLocation] = useState(editData?.location ?? "");
  const [productCodes, setProductCodes] = useState({});

  const handleErrors = (err) => {
    if (err.response?.status === 404) {
      return;
    }
    showErrorToast(toast, err.message);
  };

  useGetProductCategoriesPerLocation({ location: refetchLocation }, {
      onSuccess: (data) => {
        setProductCodes(prevState => {
          prevState[refetchLocation] = data;
          return prevState;
        });
        reset({
          [fieldArrayName]: watchFieldArray
        });
      },
      onError: err => handleErrors(err)
    }
  );

  const getEmptyRow = () => {
    return {
      productCategoryId: "",
      supplierName: "",
      itemDescription: "",
      spentMoneyAmount: "",
      location: ""
    };
  };

  const {
    register,
    control,
    handleSubmit,
    formState: { errors },
    watch,
    resetField,
    reset
  } = useForm({
    defaultValues: {
      [fieldArrayName]: [editData ? editData : getEmptyRow()]
    }
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: fieldArrayName
  });

  const watchFieldArray = watch(fieldArrayName);

  const mapRow = (row, index) => {
    const gridItems = [];

    gridItems.push(
      <FormInput
        errors={errors}
        {...register(`${fieldArrayName}.${index}.supplierName`, {
          required: globalTranslation("entityRequired", { entityName: t(`inputs.${tabKey}.supplierName`) })
        })}
        title={watchFieldArray[index]["supplierName"]}
      />
    );

    const locationSelector = register(`${fieldArrayName}.${index}.location`, {
      required: globalTranslation("entityRequired",
        { entityName: t("inputs.purchasedGoodsAndServices.location") })
    });

    gridItems.push(
      <FormSelect
        errors={errors}
        {...locationSelector}
        onChange={(e) => {
          locationSelector.onChange(e);
          setRefetchLocation(e.target.value ?? "");
          resetField(`${fieldArrayName}.${index}.productCategoryId`, { defaultValue: "" });
        }}
        title={watchFieldArray[index]["location"] !== "" && locations &&
          locations.find(loc => loc.location.toString() === watchFieldArray[index]["location"].toString()).location}
      >
        {locations && locations.length > 0 ?
          locations.map(loc => {
            return <option key={`${loc.id}-${loc.location}`} value={loc.location}>
              {loc.location}
            </option>;
          }) : []
        }
      </FormSelect>
    );

    gridItems.push(
      <FormSelect
        errors={errors}
        {...register(`${fieldArrayName}.${index}.productCategoryId`, {
          required: globalTranslation("entityRequired",
            { entityName: t("inputs.purchasedGoodsAndServices.productCode") })
        })}
        title={getProductCodeTitle(watchFieldArray, index, productCodes)}
        placeholder=" "
      >
        {Object.keys(productCodes).length > 0 && productCodes[watchFieldArray[index].location] &&
          productCodes[watchFieldArray[index].location].map(el => {
            return <option key={`${el.id}-${el.name}`} value={el.id}>
              {el.code} - {el.name}
            </option>;
          })
        }
      </FormSelect>
    );

    gridItems.push(
      <FormInput
        errors={errors}
        {...register(`${fieldArrayName}.${index}.itemDescription`)}
        title={watchFieldArray[index]["itemDescription"]}
      />
    );

    gridItems.push(
      <NumericFormInputGroup
        {...register(`${fieldArrayName}.${index}.spentMoneyAmount`, {
          required: globalTranslation("entityRequired",
            {
              entityName: watchFieldArray[index].location !== "" ?
                t(`inputs.${tabKey}.spentMoneyAmount`,
                  { currencySymbol: CURRENCY_SYMBOLS[getCurrencyCode(watchFieldArray, index, locations)] ?? "" }) :
                t(`inputs.${tabKey}.spentAmount`)
            })
        })}
        errors={errors}
        title={watchFieldArray[index]["spentMoneyAmount"]}
        onKeyDown={(e) => nonNumericSymbols.includes(e.key) && e.preventDefault()}
        type="number"
        addonText={CURRENCY_SYMBOLS[getCurrencyCode(watchFieldArray, index, locations)] ?? ""}
      />
    );

    return gridItems;
  };

  return (
    <CategoryModal
      modalData={modalData}
      formData={{ handleSubmit, fields, append, remove }}
      attributesOrder={attributesOrder}
      isOpen={isOpen}
      onClose={onClose}
      onSubmit={onSubmit}
      getEmptyRow={getEmptyRow}
      mapRow={mapRow}
    />
  );

}

export default PurchasedAndCapitalGoodsModalWrapper;